<script setup lang="ts">
import type { TabList } from '@solfacil/girassol/dist/types/components/tabs/types'
import type { User } from '~/utils/auth/User'
import type { Project } from '~/utils/project/Project'
import type { Addons, ParsedInstallment } from '~/utils/simulation/Simulation'
import type { InstallmentResponse } from '~/utils/simulation/SimulationApi'
import { useToast } from '@solfacil/girassol'
import SimulationProductsInsurances from '~/components/simulation/products/Insurances.vue'
import SuppliersModal from '~/components/simulation/SuppliersModal.vue'
import { useMoney } from '~/composables/money'
import { FEATURE_FLAGS } from '~/constants'
import SimulatorService from '~/services/simulator/Simulator'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useGetShoppingCartId from '~/store-v2/simulation/useGetShoppingCartId'
import filtersStoreMarketFinancing from '~/utils/filtersStoreMarketFinancing'
import { calculateFirstDueDate } from '~/utils/simulation/calculations'

const props = defineProps<{
  project: Project
  defaultInstallment: number
  defaultCommission: number
  defaultCommissionRaw: number
  defaultGracePeriod: number
  defaultDueDay?: number | null
  defaultFirstDueDate?: string | null
  disableFields: boolean
  showCommissionMoney: boolean
  simulation: any
  cet: string
}>()

const emit = defineEmits<{
  openComboConnectModal: ['benefits' | 'inverters']
}>()
const { createErrorToast, createInformativeToast } = useToast()
const { getAddonsApplieds } = useAddonStore()
const { setGracePeriod, setCommission } = useResumeStore()
const showEmptyStateNotInstallments = ref(false)
const cart_id = ref('')
const ENV = useEnvironment()
const user: User = useAppStorage().get('user')
const { formatMoney } = useMoney()
const CHANNEL = 'plugin-financiamento'
const route = useRoute<any>()
const projectId = ref(String(route.params.id))
const showComponentStoreMarketFinancing = ref(false)
const enabled = ref(true)
const showSuppliersModal = ref(false)

const { data: shoppingCartId } = useGetShoppingCartId(projectId.value, enabled)

onMounted(async () => {
  setSelectedTab({ key: props.defaultInstallment, label: `${props.defaultInstallment}x`, active: true })
  cart_id.value = shoppingCartId.value || ''
  showStoreMarketFinancing()
})

// Feature flags
const { data: showComboCommissionInstallmentData } = useFeatureFlag<boolean>(FEATURE_FLAGS.COMMI_COMBO_INSTALLMENT, false)
const { data: flagStoreMarketFinancingData } = useFeatureFlag<boolean>(FEATURE_FLAGS.STORE_SHOWCASE, false)
const { data: enableDueDateChangeData } = useFeatureFlag<boolean>(FEATURE_FLAGS.ENABLE_DUE_DATE_CHANGE, false, true)
const showComboCommissionInstallmentValue = computed(() => showComboCommissionInstallmentData.value || false)
const showComponentStoreMarketFinancingValue = computed(() => Boolean(flagStoreMarketFinancingData.value))
const enableDueDateChangeValue = computed(() => enableDueDateChangeData.value || false)

const inactiveFinancing = 'INACTIVE'

const { t } = useI18n()

function mountTabs(disabled?: boolean): TabList[] {
  const installmentsSteps = 12
  const min = props.project.min_installments || 12
  const max = props.project.max_installments
  const tabs: number[] = Array.from({ length: (max - min) / installmentsSteps + 1 }, (_, i) => min + i * installmentsSteps)

  const mountedTabs: TabList[] = tabs.map((installment, index) => {
    return {
      key: installment.toString(),
      label: `${installment}x`,
      active: index === (props.defaultInstallment / installmentsSteps) - (min / installmentsSteps),
      disabled,
    }
  })

  return mountedTabs
}

// installments details
const cetTab = ref(props.cet === 'POS_FIXADO' ? 'POST' : 'PRE')

function setCetTab(type: string) {
  cetTab.value = type
  requestInstallments()
}

const isProjectDisabled = props.project?.project_status === inactiveFinancing

const tabListModel = ref(mountTabs(isProjectDisabled))
const selectedInstallment = ref<ParsedInstallment | null>(null)

const projectIsCompleted = ref<boolean>(props.project.complete)

const tabSelected = ref(props.defaultInstallment)
let tabSelecteds = `tab:${props.defaultInstallment}`

const projectValue = ref(props.project.selected_installments.financing_value)
const downPayment = ref(props.project.selected_installments.down_payment)
const hasAmperaInSelectedInstallment = ref(Boolean(props.project.selected_installments.addons?.find(item => item.product_slug === 'Ampera')))

const simulatorService = new SimulatorService()

const defaultListOfInstallments = ref<ParsedInstallment[] | []>([])
const listOfInstallments = ref<InstallmentResponse | null>(null)

const loadingDetails = ref(false)

const hasProjectValueError = ref(false)

const projectIsFinished = ref(false)

const loadingAddons = ref(false)
const loadingAmperaAddon = ref(false)
const loadingInsuranceAddon = ref(false)
const removeResume = ref(true)

const selectedGracePeriod = ref({ name: `${props.defaultGracePeriod} ${(props.defaultGracePeriod === 1) ? 'Mês' : 'Meses'}`, value: props.defaultGracePeriod })

function getDefaultCommissionText() {
  const commissionText = `${props.defaultCommission} %`
  return props.showCommissionMoney && props.defaultCommissionRaw !== null
    ? `${commissionText} = ${formatMoney(props.defaultCommissionRaw)}`
    : commissionText
}

const selectedCommission = ref({
  name: getDefaultCommissionText(),
  value: props.defaultCommission,
})

const selectedDueDay = ref(props.defaultDueDay ?? 1)
const dueDayTouched = ref(false)

const firstDueDate = computed(() => {
  return calculateFirstDueDate(
    props.defaultFirstDueDate,
    props.disableFields,
    selectedGracePeriod.value.value,
    selectedDueDay.value,
  )
})

onBeforeMount(() => {
  setCommission({ name: getDefaultCommissionText(), value: props.defaultCommission })
  setGracePeriod({ name: `${props.defaultGracePeriod} ${(props.defaultGracePeriod === 1) ? 'Mês' : 'Meses'}`, value: props.defaultGracePeriod })
})

function setProjectValue(valueOfProject: number) {
  projectValue.value = valueOfProject
}

function setDownPayment(valueOfDownPayment: number) {
  downPayment.value = valueOfDownPayment
}

function setProjectValueError(error: boolean) {
  hasProjectValueError.value = error
}

const { setAmpera, setInsurance, addons_list, getAmperaValue, toggleCombo, setCombo, clearAllAppliedAddons } = useAddonStore()
const { setSelectedInstallment, getSelectedInstallment, getInstallmentSelectedByUserAction } = useResumeStore()

async function setSelected(value: ParsedInstallment, is_user_action?: boolean) {
  try {
    loadingAddons.value = true
    selectedInstallment.value = value

    if (value.combo) {
      if (listOfInstallments.value) {
        if (value.is_from_suggested) {
          setCombo(listOfInstallments.value?.suggested_simulation.addons)
          if (value.combo_conecte) {
            const comboConnectAddon = listOfInstallments.value?.suggested_simulation.addons.find(addon =>
              addon.type === 'combo_connect' || addon.product_slug === 'Combo Connect',
            )
            if (comboConnectAddon) {
              toggleCombo(comboConnectAddon.product_slug || 'Combo Connect')
            }
          }
          else {
            const comboAddon = listOfInstallments.value?.suggested_simulation.addons.find(addon =>
              addon.type === 'combo',
            )
            if (comboAddon) {
              toggleCombo(comboAddon.product_slug || 'combo')
            }
          }
        }
        else {
          setCombo(listOfInstallments.value?.original_simulation.addons)
          if (value.combo_conecte) {
            const comboConnectAddon = listOfInstallments.value?.original_simulation.addons.find(addon =>
              addon.type === 'combo_connect' || addon.product_slug === 'Combo Connect',
            )
            if (comboConnectAddon) {
              toggleCombo(comboConnectAddon.product_slug || 'Combo Connect')
            }
          }
          else {
            const comboAddon = listOfInstallments.value?.original_simulation.addons.find(addon =>
              addon.type === 'combo',
            )
            if (comboAddon) {
              toggleCombo(comboAddon.product_slug || 'combo')
            }
          }
        }
      }
    }
    else {
      clearAllAppliedAddons()
      setCombo([])
    }

    setSelectedInstallment(value, is_user_action)

    const ampera = await requestAmpera(value.simulation_id, value.engine_simulation_id, value.installments_value)

    if (!ampera || !hasAmperaInSelectedInstallment.value)
      setAmpera([])

    if (ampera) {
      setAmpera(ampera, hasAmperaInSelectedInstallment.value, props.project?.can_select_ampera)

      if (getAmperaValue()[0].applied) {
        const insurance = await requestInsurance(value.simulation_id, getAmperaValue()[0].engine_simulation_id, value.installments_value + getAmperaValue()[0].installment_price)
        if (!insurance)
          setInsurance([])
        if (insurance) {
          const currentInsuranceState = getAddonsApplieds().filter(addon =>
            addon.type === 'insurance'
            || addon.product_slug === 'Riscos Diversos'
            || addon.product_slug === 'Prestamista',
          )

          const updatedInsurance = insurance.map(ins => ({
            ...ins,
            applied: currentInsuranceState.length > 0
              ? currentInsuranceState.some(currentIns =>
                  currentIns.product_slug === ins.product_slug && currentIns.applied,
                )
              : props.project?.selected_installments?.addons?.some(projectIns =>
                projectIns.product_slug === ins.product_slug && projectIns.applied,
              ) || false,
          }))

          setInsurance(updatedInsurance)
        }
        return
      }

      const insurance = await requestInsurance(value.simulation_id, value.engine_simulation_id, value.installments_value)
      if (!insurance)
        setInsurance([])
      if (insurance) {
        const currentInsuranceState = getAddonsApplieds().filter(addon =>
          addon.type === 'insurance'
          || addon.product_slug === 'Riscos Diversos'
          || addon.product_slug === 'Prestamista',
        )

        const updatedInsurance = insurance.map(ins => ({
          ...ins,
          applied: currentInsuranceState.length > 0
            ? currentInsuranceState.some(currentIns =>
                currentIns.product_slug === ins.product_slug && currentIns.applied,
              )
            : props.project?.selected_installments?.addons?.some(projectIns =>
              projectIns.product_slug === ins.product_slug && projectIns.applied,
            ) || false,
        }))

        setInsurance(updatedInsurance)
      }
    }

    removeResume.value = false
    loadingAddons.value = false
  }
  catch {
    removeResume.value = false
    loadingAddons.value = false
  }
  finally {
    removeResume.value = false
    loadingAddons.value = false
  }
}

function setSelectedTab(value: any) {
  tabSelecteds = `tab:${value.key}`

  tabSelected.value = value.key
  requestInstallments()
}

async function requestInsurance(simulation_id: string, engine_simulation_id: string, installment_value: number): Promise<Addons[] | null> {
  try {
    const selectedInstallment = getSelectedInstallment()
    const has_combo = selectedInstallment?.combo || false
    const has_combo_connect = selectedInstallment?.combo_conecte || false
    const has_ampera = !!(getAddonsApplieds()?.find(item => item.product_slug === 'Ampera'))

    const response = await simulatorService.get_insurance(simulation_id, engine_simulation_id, installment_value, has_combo, has_combo_connect, has_ampera)
    if (response.data)
      return response.data

    return null
  }
  catch {
    return null
  }
}

async function requestAmpera(simulation_id: string, engine_simulation_id: string, installment_value: number): Promise<Addons[] | null> {
  try {
    const selectedInstallment = getSelectedInstallment()
    const has_combo = selectedInstallment?.combo || false
    const has_combo_connect = selectedInstallment?.combo_conecte || false

    const response = await simulatorService.get_ampera(simulation_id, engine_simulation_id, installment_value, has_combo, has_combo_connect)
    if (response.data)
      return response.data

    return null
  }
  catch {
    return null
  }
}

async function requestInstallments() {
  try {
    selectedInstallment.value = null
    loadingDetails.value = true

    const requestPayload = {
      project_id: props.project.id,
      person_type: props.project.person_type,
      document: props.project.document,
      project_value: projectValue.value,
      grace_period: selectedGracePeriod.value.value,
      due_day: enableDueDateChangeValue.value ? (selectedDueDay.value ?? 1) : null,
      commission: selectedCommission.value.value,
      down_payment: downPayment.value,
      cpf_risk: props.project.risk_class,
      project_name: props.project.project_name,
      partner_id: user?.parceiro?.id,
      user_id: user?.id,
      partner_level: user?.parceiro?.nivelSolfacilMais || '',
      approved_value: props.project.pre_approved_value,
      selected_installment: tabSelected.value,
      indexer_kind: cetTab.value,
      products_applieds: [''],
      installments: [0],
      origin_id: 2,
      is_ampera: !!(getAddonsApplieds()?.find(item => item.product_slug === 'Ampera')),
      suggestion_installment_enabled: Boolean(showComboCommissionInstallmentValue.value),
    }

    showEmptyStateNotInstallments.value = false

    const response = await simulatorService.calculate_project(requestPayload)

    if (response?.data) {
      setCombo(response.data.original_simulation.addons)
      listOfInstallments.value = response.data
    }
  }
  catch (e: any) {
    if (e.status === 422)
      showEmptyStateNotInstallments.value = true

    listOfInstallments.value = null
  }
  finally {
    loadingDetails.value = false
  }
}

async function showStoreMarketFinancing() {
  showComponentStoreMarketFinancing.value = showComponentStoreMarketFinancingValue.value
}

function selectGracePeriod(value: any) {
  selectedGracePeriod.value = value
  setGracePeriod(value)
  requestInstallments()
}
function selectCommission(value: any) {
  selectedCommission.value = value
  setCommission(value)
  requestInstallments()
}

function selectDueDay(day: number) {
  dueDayTouched.value = true
  selectedDueDay.value = day
  requestInstallments()
}

watch(() => props.defaultDueDay, (next, prev) => {
  if (dueDayTouched.value || next == null || selectedDueDay.value === next)
    return

  selectedDueDay.value = next
  if (prev !== undefined && prev !== next)
    requestInstallments()
})

function getMessageOfCet(selectedCet: string) {
  if (selectedCet === 'POST')
    return 'As parcelas passam por reajustes mensais decorrentes do IPCA'

  return 'As parcelas são fixas e não sofrem alteração de valor durante todo o financiamento'
}

function onAddKit(data: { cartId: any, kit: any }) {
  cart_id.value = String(data.cartId)
  updateShoppingCartId(props.project.id, cart_id.value)
  createInformativeToast({
    title: t('toastKitSelected.title'),
    description: t('toastKitSelected.description'),
  })
}

function onDeleteKit() {
  cart_id.value = ''
}

function getPreselectedOptions(project: Project) {
  if (getInstallmentSelectedByUserAction()) {
    const selectedInstallment = getInstallmentSelectedByUserAction()
    if (selectedInstallment && selectedInstallment.combo_conecte) return 'with_combo_conecte'
    return selectedInstallment?.combo ? selectedInstallment?.is_from_suggested ? 'with_combo_comission' : 'with_combo' : 'without_combo'
  }

  const projectInstallment = project?.selected_installments
  const hasComboConnect = projectInstallment?.addons?.some(addon => addon.type === 'combo_connect') || projectInstallment?.is_combo_connect
  if (hasComboConnect) return 'with_combo_conecte'
  const typeInstallment = projectInstallment?.is_combo ? projectInstallment.is_from_suggested ? 'with_combo_comission' : 'with_combo' : 'without_combo'
  return typeInstallment
}

async function updateShoppingCartId(projectId: string, cartId: string) {
  const payload = {
    shopping_cart_id: cartId,
  }

  try {
    await simulatorService.put_simulation_by_shopping_cart_id(projectId, payload)
  }
  catch (error) {
    console.error(error)
    createErrorToast('Não foi possível realizar a ação. Por favor, tente novamente.')
  }
  finally {
    loadingAddons.value = false
  }
}

const projectIsFormalized = !!props.project.is_formalized

function isPostFixed() {
  return props.cet && props.cet !== 'PRE_FIXADO'
}

const shouldShowStoreMarketFinancingComponent = computed(() => {
  return showComponentStoreMarketFinancing.value && props.project.system_type !== 'with_battery' && shoppingCartId.value !== undefined
})
</script>

<template>
  <section class="revision-container">
    <div class="mb-20 lg:site:mb-0">
      <div class="details-container !mt-0">
        <SolAlert
          v-if="isProjectDisabled"
          id="error-feedback"
          class="lg:site:mb-6"
          title="Financiamento inativo."
          text="Este financiamento está inativo no momento."
          feedback="error"
        />
        <div>
          <h2 class="fonts-body-x-large-bold mb-2">
            {{ t("revision.title") }}
          </h2>
          <p class="fonts-subtitle-small text-brand-primary-medium">
            {{ t("revision.subtitle") }} <span class="font-bold">{{ t("revision.subtitle_complement") }}</span>
          </p>
        </div>
        <div class="container-details">
          <SimulationElementProjectFinancialInfo
            :select-commission="selectCommission"
            :max-grace-period="props.project?.max_grace_period"
            :select-grace-period="selectGracePeriod"
            :disable-fields="!loadingDetails && !isProjectDisabled"
            :loading-details="loadingDetails"
            :selected-commission="selectedCommission"
            :selected-grace-period="selectedGracePeriod"
            :selected-due-day="selectedDueDay"
            :select-due-day="selectDueDay"
            :project-id="props.project.id"
            :default-first-due-date="firstDueDate"
          />
          <SimulationElementProjectValue
            v-if="(projectValue && downPayment) >= 0"
            :set-project-value-error="setProjectValueError"
            :select-project-value="setProjectValue"
            :max-grace-period="props.project?.max_grace_period"
            :select-down-payment="setDownPayment"
            :disable-fields="loadingDetails || isProjectDisabled"
            :project="project"
            :loading-details="loadingDetails"
            :project-value-chosen="projectValue"
            :down-payment-chosen="downPayment"
            :request-installments="requestInstallments"
          />
        </div>
      </div>
      <div class="mb-2xs">
        <StoreMarketFinancing
          v-if="shouldShowStoreMarketFinancingComponent"
          title="Kits disponíveis no Combo Fácil"
          subtitle="Sugestões da nossa loja de acordo com as dimensões do seu projeto."
          :channel="CHANNEL"
          :environment="ENV"
          :class="{ 'pointer-events-none': projectIsFormalized }"
          :partner-id="user?.parceiro?.id"
          :cart-id="shoppingCartId || cart_id"
          :filters="filtersStoreMarketFinancing.createObject(`${(project.system_power)}`, project.project_state)"
          :segmentation-id="props.project.id"
          :disable-filters="projectIsFormalized"
          :disable-cart="projectIsFormalized"
          mix-panel-env="Revision"
          @on-add-kit="onAddKit"
          @on-delete-kit="onDeleteKit"
        />
      </div>

      <div class="details-container">
        <div class="mb-2xs gap-1">
          <div class="flex justify-between">
            <div class="mr-4xs">
              <h3 class="card-subtitle mb-nano">
                Opções de parcela
              </h3>
              <p
                v-if="isPostFixed()"
                class="fonts-subtitle-small text-neutral-low-light"
              >
                Pesquise entre nossas opções de prazo a que melhor se encaixa no perfil do seu cliente
              </p>
            </div>
            <SimulationElementDownloadList
              v-if="!projectIsCompleted"
              :commission="selectedCommission.value"
              :grace-period="selectedGracePeriod.value"
              :is-combo="getSelectedInstallment()?.combo"
              :due-day="selectedDueDay"
              :first-due-date="firstDueDate"
            />
          </div>

          <p
            v-if="!props.cet || props.cet === 'PRE_FIXADO'"
            class="fonts-subtitle-small text-neutral-low-light"
          >
            Pesquise entre nossas opções a que melhor se encaixa no perfil do seu cliente. Todas nossas parcelas são prefixadas, ou seja, não sofrem alteração de valor durante todo o financiamento.
          </p>

          <div
            v-if="isPostFixed()"
            class="flex py-4xs"
          >
            <SimulationElementSelectOptionsOfCet
              :active="cetTab"
              :disabled="!!(props.project?.project_status === 'INACTIVE')"
              :cet="cet"
              @selected="setCetTab"
            />
          </div>
          <p
            v-if="isPostFixed()"
            class="fonts-subtitle-small text-neutral-low-light"
          >
            {{ getMessageOfCet(cetTab) }}
          </p>
        </div>
        <BlockFieldsProxy :block-fields="loadingDetails">
          <SolTabs id="simple" v-model:tab-list="tabListModel" class="tabscet_parsed" @tabitem:active="setSelectedTab">
            <template #[tabSelecteds]>
              <div v-if="!loadingDetails">
                <SimulationElementInstallmentList
                  :project="project"
                  :set-selected="setSelected"
                  :loading-addons="loadingAddons || loadingInsuranceAddon"
                  :loading-details="loadingDetails"
                  :disable-fields="props.disableFields"
                  :show-empty-state-not-installments="showEmptyStateNotInstallments"
                  :is-project-disabled="isProjectDisabled"
                  :installment-list="listOfInstallments"
                  :refetch-installment="requestInstallments"
                  :has-default-installment="!defaultListOfInstallments.length"
                  :pre-selected-installment="getPreselectedOptions(props.project)"
                  :financing-value="props.project.project_value - props.project.down_payment"
                  :person-type="props.project.person_type"
                  :selected-installments="tabSelected"
                  @open-combo-connect-modal="emit('openComboConnectModal', $event)"
                  @open-suppliers-modal="showSuppliersModal = true"
                />
              </div>
              <div v-if="loadingDetails">
                <div class="loader-installment" />
              </div>
            </template>
          </SolTabs>
        </BlockFieldsProxy>
      </div>

      <div class="mb-8 lg:site:mb-2xs">
        <div v-if="loadingAmperaAddon || loadingAddons" class="my-4 card-container flex flex-col justify-between w-full">
          <span class="loader-project" />
        </div>

        <!-- <SimulationProductsAmpera
          v-if="selectedInstallment && (!loadingAmperaAddon && !loadingAddons)"
          :refetch-insurance="refetchInsurance"
          :loading="loadingAmperaAddon"
          :project-is-completed="props.disableFields"
          :can-select-ampera="props.project?.can_select_ampera"
        /> -->

        <div v-if="loadingInsuranceAddon || loadingAddons" class="my-4 card-container flex flex-col justify-between w-full">
          <span class="loader-project" />
        </div>

        <SimulationProductsInsurances
          v-if="selectedInstallment && (!loadingInsuranceAddon && !loadingAddons)"
          :loading="loadingInsuranceAddon"
          :project-is-completed="props.disableFields"
          :installment="selectedInstallment"
          :project-selected-installments-addons="props.project?.selected_installments?.addons"
          :project-id="props.project?.id"
        />
        <div v-else-if="addons_list === null && selectedInstallment" class="empty-insurance card-container">
          <img src="/images/not-found.png" class="not-found mb-4xs">
          <h1 class="text-brand-primary-dark fonts-heading-h2 mb-4xs">
            Erro ao carregar seguros
          </h1>
          <p class="fonts-body-x-large-regular text-brand-primary-medium mb-4xs">
            Desculpe, não conseguimos exibir as opções de seguros no momento. Você pode seguir com o financiamento ou tentar novamente em alguns minutos.
          </p>
        </div>
      </div>
      <SimulationRevisionResumeButtons
        v-if="!removeResume"
        :loading-conclusion="loadingInsuranceAddon || loadingAddons"
        :disabled-fields="!disableFields"
        :shopping-cart-id="shoppingCartId || cart_id"
        :cart-id-selected="cart_id"
        :default-grace-period="props.defaultGracePeriod"
        :selected-grace-period="selectedGracePeriod.value"
        :default-due-day="props.defaultDueDay ?? 1"
        :selected-due-day="selectedDueDay"
      />
    </div>
    <SimulationElementResume
      :project-is-finished="projectIsFinished"
      :disabled-fields="props.disableFields"
      :selected-commission="selectedCommission.value"
      :selected-grace-period="selectedGracePeriod.value"
      :selected-due-day="selectedDueDay"
      :default-first-due-date="firstDueDate"
      :project="props.project"
      class="hidden md:system:block"
      :installment="selectedInstallment"
      :loading-conclusion="loadingAddons"
      :project-value="projectValue"
      :project-downpayment="downPayment"
      :is-details="false"
    />
    <SolAccordion id="resume-mobile" title="Resumo" class="accordion-mobile">
      <SimulationElementResume
        :loading-conclusion="loadingInsuranceAddon || loadingAmperaAddon"
        :project-is-finished="projectIsFinished"
        :disabled-fields="props.disableFields"
        :selected-commission="selectedCommission.value"
        :selected-grace-period="selectedGracePeriod.value"
        :selected-due-day="selectedDueDay"
        :default-first-due-date="firstDueDate"
        :project="props.project"
        class="col-span-3"
        :installment="selectedInstallment"
        :project-value="projectValue"
        :project-downpayment="downPayment"
        :is-details="false"
      />
    </SolAccordion>
    <SuppliersModal v-model:is-open="showSuppliersModal" />
  </section>
</template>

<style lang="scss">
.revision-container{
  @apply block w-full;
  @screen md:system {
    @apply grid gap-4;
    grid-template-columns: minmax(0, 1fr) 300px
  }

  .empty-insurance{
    @apply flex flex-col justify-center text-center items-center py-md;
  }
  .accordion-mobile {
    @apply fixed left-0 bottom-0 w-full text-center rounded-t-lg bg-neutral-high-pure z-20;
    box-shadow: 0 0 5px rgba(0, 0, 0, .1);
    @screen md:system {
      @apply hidden;
    }
    .resume-container {
      @apply px-4xs;
      h2 {
        @apply hidden;
      }
      h3 {
        @apply text-left;
      }
    }
  }
  .accordion-content {
    height: 60vh;
    @apply overflow-scroll;
  }
  .container-title {
    @apply fonts-heading-h3;
    color: #222222;
  }
  .sol-accordion-core>.summary {
    @apply h-18;
  }
  .sol-accordion-core>.summary>.icon {
    @apply rotate-180;
  }
  .sol-accordion-core[open]>.summary>.icon {
    @apply rotate-0;
  }
  .list-style-combo{
    li{
      @apply bg-brand-secondary-light;
    }
    .-selected {
      @apply bg-brand-secondary-light;
    }
  }

  .loader-installment{
    @apply h-24 w-full;
    @apply top-0 right-0 bottom-0 left-0 z-50;
    background: rgba(#fff, 0.9)
      url(https://cdn.solfacil.com.br/assets/img/loading-for-legacy.gif) no-repeat
      center center;
  }

  .tabs {
    @apply mb-3xl;
  }

  .tabscet_parsed {
    overflow: visible !important;

    :deep(.sol-tabs-core) {
      overflow: visible !important;
    }

    :deep(.sol-tabs-content) {
      overflow: visible !important;
    }
  }

  .list-style{
    .-selected {
      @apply bg-neutral-high-pure;
    }
  }

  .cet-select-left{
    @apply p-nano rounded-l-lg border border-brand-primary-light
  }
  .cet-select-right{
    @apply p-nano rounded-r-lg border border-brand-primary-light
  }

  .details-container {
    @apply flex justify-between w-full flex-col;
    @apply px-4xs my-6 mt-0 bg-neutral-high-pure rounded-lg;
    @screen md:site {
      @apply p-8 mt-6 mb-2xs;
    }

    .container-details{
      @apply w-full py-2xs my-0 flex flex-col;
      @screen md:site {
        @apply my-4xs;
      }
      .details{
        @apply flex justify-between py-nano border-b-neutral-high-medium border-b-1;
      }
    }
  }

  .ticker{
    @apply fonts-body-small-bold px-micro py-quark bg-brand-secondary-medium text-feedback-positive-dark rounded-full font-bold ml-auto;
    @screen md:site {
      @apply ml-0;
    }
  }
  .list-item>.list-item-row {
    @apply pt-4xs;
    @screen md:site {
      @apply pt-nano;
    }
  }
  .list-item>.list-item-row>.column-item:nth-of-type(2),
  .list-item>.list-item-row>.column-item:nth-of-type(3) {
    @apply mr-0;
    @screen md:site {
    @apply mr-4xs;
    }
  }
}
</style>
